import { z } from "zod";
// 🔴 REUSE enum status đã có ở `collections.ts` — KHÔNG khai whitelist thứ hai. Hai bản khai riêng
// rồi lệch nhau chính là mẫu bug mà epic này đang đi vá (drift 2 whitelist, xem US-AE16.F1-0369).
import { paymentConfirmationStatusSchema } from "./collections.js";
import { adminListFilterShape, refineAdminDayRange } from "./admin-list-filters.js";

/**
 * Shared DTO **Admin Payment Confirmations** (US-AE16.F3-0371-S01 · @UC9637 @UC9638).
 *
 * 🔴🔴 VÌ SAO CÓ SURFACE NÀY — lỗ hỗ trợ khách hàng lớn nhất của portal.
 *
 * Ba trạng thái tiền (§3.7) là: (1) **nghĩa vụ**, (2) **khai đã trả**, (3) **xác nhận đã nhận**.
 * Admin trước task này chỉ thấy (1) dạng ĐẾM và một phần (3) dạng latency — **(2) hoàn toàn vô hình**:
 * `paymentConfirmation` xuất hiện đúng 2 file trong `admin-*`, không file nào là list surface. Không
 * có endpoint nào trả lời được *"đợt thu này ai đã khai đã chuyển?"*.
 *
 * Hệ quả cụ thể: `rejectReason` được GHI ở `approve-decline.service.ts` nhưng **0 chỗ đọc ở admin**
 * ⇒ SUPPORT nhận đúng câu khiếu nại phổ biến nhất — *"tôi chuyển rồi, ảnh gửi rồi, sao bị từ chối?"*
 * — và **không tra được gì**.
 *
 * 🔴 MA TRẬN REDACTION (QĐ PO 06/08 — fail-closed):
 *   - **tiền** (`claimedAmountMinor`): `SUPER_ADMIN` + `OPS` (= `canViewMoney`). Vai khác ⇒ `null`.
 *   - **danh tính** (`memberDisplayName`): `SUPER_ADMIN` + `SUPPORT` (= `canViewPii`) — SUPPORT là
 *     vai *cần* dữ liệu này nhất để trả lời khiếu nại. Vai khác ⇒ `null`.
 *   - **`rejectReason`** (chữ người dùng gõ ⇒ PII): **CHỈ `SUPER_ADMIN`**. Vai khác ⇒ `null` +
 *     `reasonRedacted: true`. Khuôn này COPY nguyên từ `ai-audit.service.ts:127` (`rawPrompt` chỉ
 *     SUPER_ADMIN xem thô, vai khác `null` + `promptRedacted`) — **đi theo khuôn đang có, KHÔNG dựng
 *     khuôn thứ hai**.
 *
 * Ẩn = `null`, KHÔNG `'•••'`, KHÔNG `0`. `"0"` (khai 0 đồng) và `null` (không được xem) là hai sự
 * thật khác nhau. Số/chuỗi bị ẩn **không rời API**.
 */

export const ADMIN_PAYMENT_CONFIRMATIONS_MAX_LIMIT = 100;

/**
 * ⚠️ Status của `payment_confirmations` là `submitted|approved|rejected` (KHÔNG phải `pending`):
 * `submitted` = người đóng đã khai, chưa ai đối chiếu — chính là **trạng thái (2)** của §3.7.
 * Enum lấy từ `collections.ts` (REUSE, không khai lại).
 */

export const adminPaymentConfirmationsQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    collectionId: z.string().uuid().optional(),
    status: paymentConfirmationStatusSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(ADMIN_PAYMENT_CONFIRMATIONS_MAX_LIMIT)
      .optional(),
    // US-AE4.F7-1006 (bổ sung): ô tìm + khoảng ngày (theo lúc gửi yêu cầu) — xem `admin-list-filters.ts`.
    ...adminListFilterShape,
  })
  .strict()
  .superRefine(refineAdminDayRange);

export type AdminPaymentConfirmationsQuery = z.infer<typeof adminPaymentConfirmationsQuerySchema>;

export const adminPaymentConfirmationItemSchema = z
  .object({
    id: z.string(),
    memberDueId: z.string(),
    collectionId: z.string(),
    collectionTitle: z.string().nullable(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string(),
    groupName: z.string().nullable(),
    /** Danh tính người khai — redact theo `canViewPii` (SUPER_ADMIN/SUPPORT). */
    memberDisplayName: z.string().nullable(),
    /**
     * US-AE4.F7-0909 (@UC9949) — TÀI KHOẢN đã bấm "Tôi đã chuyển" (`submittedBy`), khác `memberDisplayName`
     * là tên SUẤT (nhóm phụ huynh: tên bé). Cùng cổng `canViewPii`; vai khác / không tra được ⇒ `null`.
     */
    submitterDisplayName: z.string().nullable(),
    /**
     * US-AE4.F7-0909 (@UC9949) — kỳ thứ mấy của đợt định kỳ, cùng phép xếp hạng `loadPeriodIndexes` với
     * feed app ("Kỳ N"). Đợt một lần ⇒ `null`.
     */
    collectionPeriodIndex: z.number().int().positive().nullable(),
    status: paymentConfirmationStatusSchema,
    /** BigInt minor → string; `null` = KHÔNG ĐƯỢC XEM (khác `"0"` = khai 0 đồng). */
    claimedAmountMinor: z.string().nullable(),
    currency: z.string(),
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
    /** Chữ người dùng gõ ⇒ PII. CHỈ SUPER_ADMIN thấy thô; vai khác `null` + `reasonRedacted`. */
    rejectReason: z.string().nullable(),
    /**
     * `true` ⇒ CÓ lý do nhưng vai này không được xem. Phân biệt với `rejectReason=null` +
     * `reasonRedacted=false` = **thật sự không có lý do**. UI phải hiện hai thứ này KHÁC nhau,
     * nếu không màn hình nói dối rằng "không ghi lý do".
     */
    reasonRedacted: z.boolean(),
    /**
     * US-AE4.F7-1006 (@UC9636) — khoản của yêu cầu ĐÃ KHÉP (đã đóng / nộp hộ / miễn). Màn dùng để nói trước: từ chối lúc
     * này chỉ là đóng yêu cầu trùng, không gửi tin. Server vẫn đọc lại trong khoá dòng lúc từ chối. `.optional()` cho response cũ.
     */
    dueClosed: z.boolean().optional(),
  })
  .strict();

export type AdminPaymentConfirmationItem = z.infer<typeof adminPaymentConfirmationItemSchema>;

export const adminPaymentConfirmationsResponseSchema = z.object({
  data: z.array(adminPaymentConfirmationItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminPaymentConfirmationsResponse = z.infer<
  typeof adminPaymentConfirmationsResponseSchema
>;

/**
 * ── S02 (@UC9639): nghĩa vụ TỪNG NGƯỜI của một đợt thu ──────────────────────────────────────────
 *
 * `admin-collections` chỉ có detail **aggregate** (`dueCountByStatus` + `collectedMinor`), cố ý
 * *"KHÔNG danh tính từng thành viên"*. Với oversight tổng quan thì đúng; với **hỗ trợ MỘT CA cụ thể**
 * thì vô dụng — SUPPORT không trả lời được "anh A đã đóng bao nhiêu rồi?".
 *
 * 🔴 `paidAmountMinor` là MẤU CHỐT: trạng thái `partially_paid` chỉ có nghĩa khi đọc được **đã đóng
 * bao nhiêu**. Trả `status='partially_paid'` mà không kèm số = vẫn vô hình.
 *
 * 🔴 `paidAmountMinor = "0"` (chưa đóng đồng nào) ≠ `null` (không được xem). Hai thứ này TUYỆT ĐỐI
 * không được gộp thành một cách hiển thị.
 */
export const ADMIN_COLLECTION_DUES_MAX_LIMIT = 200;

export const adminCollectionDuesQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_COLLECTION_DUES_MAX_LIMIT).optional(),
  })
  .strict();

export type AdminCollectionDuesQuery = z.infer<typeof adminCollectionDuesQuerySchema>;

export const adminCollectionDueItemSchema = z
  .object({
    memberDueId: z.string(),
    memberId: z.string(),
    /** Danh tính — redact theo `canViewPii`. */
    memberDisplayName: z.string().nullable(),
    status: z.string(),
    /** Số PHẢI đóng (string minor | null khi redact). */
    amountMinor: z.string().nullable(),
    /** Số ĐÃ đóng (string minor | null khi redact). `"0"` là sự thật, KHÔNG phải "không được xem". */
    paidAmountMinor: z.string().nullable(),
    currency: z.string(),
    dueDate: z.string().nullable(),
    /** Trạng thái khai báo GẦN NHẤT của due này — nối trạng thái (2) vào màn nghĩa vụ. */
    lastConfirmationStatus: paymentConfirmationStatusSchema.nullable(),
  })
  .strict();

export type AdminCollectionDueItem = z.infer<typeof adminCollectionDueItemSchema>;

export const adminCollectionDuesResponseSchema = z.object({
  data: z.array(adminCollectionDueItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminCollectionDuesResponse = z.infer<typeof adminCollectionDuesResponseSchema>;

/**
 * US-AE4.F7-1006 (@UC9636 · QĐ người dùng 02/10/2026) — nhân viên TỪ CHỐI một yêu cầu nộp tiền đang chờ. MỘT nút (gộp
 * "đóng yêu cầu thừa"): khoản còn thiếu ⇒ từ chối như thủ quỹ (lý do đi NGUYÊN VĂN vào tin gửi phụ huynh); khoản đã khép ⇒
 * đóng yêu cầu trùng với `ADMIN_DUPLICATE_CLAIM_REASON`, không gửi tin (lý do nhân viên gõ chỉ vào audit admin).
 */
export const ADMIN_PAYMENT_CONFIRMATION_REASON_MAX_LENGTH = 500;
export const adminRejectPaymentConfirmationBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(ADMIN_PAYMENT_CONFIRMATION_REASON_MAX_LENGTH),
    /**
     * Điều màn đã nói với nhân viên (`dueClosed` của dòng lúc mở hộp thoại): `true` ⇒ "chỉ đóng yêu cầu trùng, lý do chỉ vào
     * nhật ký"; `false` ⇒ "từ chối, lý do đi nguyên văn tới phụ huynh". Server đọc lại trong khoá; lệch ⇒ 409
     * `PAYMENT_CONFIRMATION_DUE_CHANGED`, không ghi gì.
     */
    expectDueClosed: z.boolean(),
  })
  .strict();
export type AdminRejectPaymentConfirmationBody = z.infer<typeof adminRejectPaymentConfirmationBodySchema>;

export const adminRejectPaymentConfirmationResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    /** Khoản đã khép lúc từ chối (đọc trong khoá) ⇒ chỉ đóng yêu cầu trùng. */
    dueClosed: z.boolean(),
    newDueStatus: z.string(),
    /** Có gửi tin "chưa được duyệt" cho người nộp không. */
    notified: z.boolean(),
  })
  .strict();
export type AdminRejectPaymentConfirmationResponse = z.infer<typeof adminRejectPaymentConfirmationResponseSchema>;

/**
 * US-AE4.F7-1040 (@UC9953 · QĐ người dùng 30/10/2026) — nhân viên DUYỆT hộ một yêu cầu nộp tiền đang chờ (đường dự phòng khi nút
 * Duyệt ở app hỏng). Gọi lại lõi duyệt của app: số vào sổ = số đã khai (không nhập số), nhãn `system_admin_assist`, người ghi sổ NULL
 * (không đứng tên ai trong nhóm). Nhân viên thật + lý do chỉ vào audit admin.
 */
export const adminApprovePaymentConfirmationBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(ADMIN_PAYMENT_CONFIRMATION_REASON_MAX_LENGTH),
  })
  .strict();
export type AdminApprovePaymentConfirmationBody = z.infer<typeof adminApprovePaymentConfirmationBodySchema>;

export const adminApprovePaymentConfirmationResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    /** Số THẬT SỰ vào sổ lượt này (string minor). */
    amountMinor: z.string().regex(/^\d+$/),
    currency: z.string().length(3),
    /** Duyệt chỉ đẻ ra được hai kết cục này (khuôn `approvePaymentResponseSchema` của app). */
    newDueStatus: z.enum(["paid", "partially_paid"]),
  })
  .strict();
export type AdminApprovePaymentConfirmationResponse = z.infer<typeof adminApprovePaymentConfirmationResponseSchema>;

/** Lý do ghi lên yêu cầu trùng (khoản đã khép) — người dùng đọc được trên app ("Đã bị từ chối — lý do: …"). */
export const ADMIN_DUPLICATE_CLAIM_REASON = "Khoản đã được ghi đủ — đóng yêu cầu trùng";
/** Khoản đổi trạng thái (khép ↔ còn thiếu) so với lúc màn hiển thị ⇒ 409; tải lại rồi làm lại. */
export const PAYMENT_CONFIRMATION_DUE_CHANGED_CODE = "PAYMENT_CONFIRMATION_DUE_CHANGED" as const;
